"use client"

import React from 'react'

interface KioskIdleTimerProps {
  remainingSeconds: number
  /** 타이머 경고 시작 시간 (기본 20초) - 이 값이 원의 100% */
  warningSeconds?: number
  onTouch: () => void
}

/**
 * 키오스크 유휴 타이머 (Figma 디자인 기반 - 절대 위치)
 *
 * Figma 기준 (2160x3840):
 * - 원형 타이머: x=805, y=1521, 514x514px
 * - 숫자 "16": x=960, y=1703, Inter 600 180px
 * - 메시지: x=393, y=2169, Pretendard 600 100px
 * - 버튼: x=330, y=3105, 1500x340px
 *
 * 타이머는 warningSeconds(20초)부터 시작하여 0까지 카운트다운
 * 20초 = 100% 원, 0초 = 0% 원
 */
export const KioskIdleTimer = ({
  remainingSeconds,
  warningSeconds = 20,
  onTouch,
}: KioskIdleTimerProps) => {
  // 프로그레스 계산: 20초 기준으로 100% → 0%
  const progress = Math.min(remainingSeconds / warningSeconds, 1)

  // SVG 원형 프로그레스 계산
  const radius = 80
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference * (1 - progress)

  // Figma 비율 계산 (2160x3840 기준)
  const figmaWidth = 2160
  const figmaHeight = 3840

  return (
    <div
      className="fixed inset-0 z-[400] pointer-events-auto"
      style={{
        backgroundColor: 'rgba(18, 19, 20, 0.75)',
        backdropFilter: 'blur(30px)',
      }}
      onClick={onTouch}
    >
      {/* 컨텐츠 컨테이너 - 9:16 비율 유지 */}
      <div
        className="relative h-full mx-auto"
        style={{
          aspectRatio: '9/16',
          maxHeight: '100%',
        }}
      >
        {/* 원형 타이머 - Figma: x=805, y=1521, 514x514 */}
        <div
          className="absolute"
          style={{
            left: `${(805 / figmaWidth) * 100}%`,
            top: `${(1521 / figmaHeight) * 100}%`,
            width: `${(514 / figmaWidth) * 100}%`,
            aspectRatio: '1/1',
          }}
        >
          {/* 배경 원 - rgba(255,255,255,0.4) */}
          <div className="absolute inset-0 rounded-full bg-white/40" />

          {/* 프로그레스 원 - 흰색 stroke 16px */}
          <svg
            className="absolute inset-0 w-full h-full -rotate-90"
            viewBox="0 0 200 200"
          >
            <circle
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-1000 ease-linear"
            />
          </svg>

          {/* 중앙 숫자 - Inter 600 180px */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className="font-inter font-semibold text-white/90"
              style={{
                fontSize: `${(180 / figmaWidth) * 100}cqw`,
                lineHeight: '0.83',
              }}
            >
              {remainingSeconds}
            </span>
          </div>
        </div>

        {/* 안내 텍스트 - Figma: x=393, y=2169, Pretendard 600 100px */}
        <p
          className="absolute font-pretendard font-semibold text-white/90 text-center"
          style={{
            left: `${(393 / figmaWidth) * 100}%`,
            top: `${(2169 / figmaHeight) * 100}%`,
            width: `${(1336 / figmaWidth) * 100}%`,
            fontSize: `${(100 / figmaWidth) * 100}cqw`,
            lineHeight: '1.5',
          }}
        >
          잠시후, 초기 화면으로 돌아갈게요!
        </p>

        {/* 계속하기 버튼 - Figma: x=330, y=3105, 1500x340 */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onTouch()
          }}
          className="absolute flex items-center justify-center bg-white active:scale-[0.98] transition-transform"
          style={{
            left: `${(330 / figmaWidth) * 100}%`,
            top: `${(3105 / figmaHeight) * 100}%`,
            width: `${(1500 / figmaWidth) * 100}%`,
            height: `${(340 / figmaHeight) * 100}%`,
            borderRadius: '9999px',
            boxShadow: '0px 0px 50px 0px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* 버튼 텍스트 - Pretendard 600 120px */}
          <span
            className="font-pretendard font-semibold"
            style={{
              color: '#0A1323',
              fontSize: `${(120 / figmaWidth) * 100}cqw`,
              lineHeight: '1.3',
            }}
          >
            계속 대화하기
          </span>
        </button>
      </div>
    </div>
  )
}

export default KioskIdleTimer
